import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Table, Row, Col } from 'react-bootstrap';
import { generateClient } from '@aws-amplify/api';
import { upsertSegment } from '@/graphql/mutations';
import ProgressHeader from '@/components/ProgressHeader';
import './NewSegmentForm.css'
import { transformSegmentStateToSchema } from '@/utils/segmentUtils';
import ErrorModal from '@/ErrorModal';

const client = generateClient();

const SegmentSummary = ({ formData: _formData = {}, links = [], setActiveForm }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [errorMessage, setErrorMessage] = useState();

	const { formData } = _formData;

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
	};

	const handleSubmit = async () => {
		const payload = transformSegmentStateToSchema(_formData);
		try {
			const result = await client.graphql({
				query: upsertSegment,
				variables: payload,
			});



			navigate('/app/customers/segments');
		} catch (error) {
			if (error?.errors[0]?.message === 'ER_DUP_ENTRY') {
				setErrorMessage('Segment name must be unique.');
			} else {
				catchError(error);
			}
		}
	};

	if (!formData) {
		return (
			<>
				<ProgressHeader step={2} />
				<div>
					<p>No data to display. Please complete the segment form first.</p>
					<Button onClick={() => navigate('/app/customers/segments')}>Back to Segment Form</Button>
				</div>
			</>
		);
	}

	const formatSubjectFilters = (filtersJson) => {
		if (!Array.isArray(filtersJson) || filtersJson.length === 0) {
			return 'N/A';
		}

		// Each object in array = OR group
		const groups = filtersJson.map(group => {
			return Object.entries(group)
				.map(([field, ops]) => {
					return Object.entries(ops)
						.map(([op, value]) => `${field} ${op} ${value}`)
						.join(' AND ');
				})
				.join(' AND ');
		});

		return groups.join(' OR ');
	};


	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<ProgressHeader step={2} />
			<div className="container mt-3">
				<h2>Summary</h2>
				<Row>
					{/**/}
					<Col md={6}>
						<Table striped bordered hover className="no-vertical-border">
							<tbody>
								<tr>
									<td><b>Segment Name</b></td>
									<td>{formData.segmentName || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Age Range</b></td>
									<td>{formData.ageRange?.join(' - ') || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Gender</b></td>
									<td>{formData.gender || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Ethnicity/Race</b></td>
									<td>{formData.ethnicity?.label || formData.ethnicity || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Language</b></td>
									<td>{formData.language?.label || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Location</b></td>
									<td>{formData.locationEnabled ? formData.position?.label || 'N/A' : 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Radius</b></td>
									{formData.locationEnabled ? (
										<td>{formData.locationRadius} {formData.measurementSystem === 'imperial' ? 'mi' : 'km'}</td>
									) : (
										<td>N/A</td>
									)}
								</tr>
								<tr>
									<td><b>BMI</b></td>
									<td>
										{formData.bmi_min !== undefined && formData.bmi_max !== undefined
											? `${formData.bmi_min} - ${formData.bmi_max}`
											: 'N/A'}
									</td>
								</tr>

								<tr>
									<td><b>Weight (Min - Max)</b></td>
									<td>{formData.weight_min_lb || 'N/A'} - {formData.weight_max_lb || 'N/A'} lbs</td>
								</tr>
								<tr>
									<td><b>Height (Min - Max)</b></td>
									<td>{formData.height_min_in || 'N/A'} - {formData.height_max_in || 'N/A'} in</td>
								</tr>
								<tr>
									<td><b>Race</b></td>
									<td>{formData.race?.label || formData.race || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Conditions Diagnosed</b></td>
									<td>{formData.conditions && formData.conditions.length > 0 ? formData.conditions.map(c => c.name || c.id || c).join(', ') : 'N/A'}</td>
								</tr>
								{/* <tr>
							<td><b>Previous Treatment or Procedures</b></td>
							<td>{formData.procedures && formData.procedures.length > 0 ? formData.procedures.map(p => p.label || p.value || p).join(', ') : 'N/A'}</td>
						</tr> */}
								<tr>
									<td><b>Current Medications</b></td>
									<td>{formData.medications && formData.medications.length > 0 ? formData.medications.map(m => m.name || m.id || m).join(', ') : 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Allergies</b></td>
									<td>{formData.allergies && formData.allergies.length > 0 ? formData.allergies.map(a => a.name || a.id || a).join(', ') : 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Smoking Status</b></td>
									<td>{formData.smoking || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Previous Trial Participation</b></td>
									<td>{formData.previousTrial || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Participation Status</b></td>
									<td>{formData.participationStatus?.label || formData.participationStatus || 'N/A'}</td>
								</tr>

								<tr>
									<td><b>Preferred Contact Method</b></td>
									<td>{formData.contactMethod || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Last Interaction Date</b></td>
									<td>{formData.lastInteraction || 'N/A'}</td>
								</tr>
								<tr>
									<td><b>Subject Filters</b></td>
									<td>
										{formData.subjectFilterEnabled
											? formatSubjectFilters(formData.subject_filters_json)
											: 'N/A'}
									</td>
								</tr>

							</tbody>
						</Table>
					</Col>

					{/*second colom Charts placeholder */}
					<Col md={6} style={{ paddingLeft: '20px' }}>
						<div style={{
							height: '100%',
							backgroundColor: '#f9f9f9',
							borderRadius: '12px',
							padding: '20px',
							boxShadow: '0 0 10px rgba(0,0,0,0.1)'
						}}>
							{/* Gauge Chart, Pie Chart, */}
							<h4>Segment Strength</h4>
							{/* */}
							<div style={{ height: 200, marginBottom: 20 }}>
								{/* Your Gauge Chart component here */}
							</div>
							{/* */}
							<div style={{ height: 200 }}>
								{/* Your Pie Chart component here */}
							</div>
						</div>
					</Col>
				</Row>

				<div className="d-flex justify-content-between mt-4">
					<Button variant="outline-secondary" onClick={() => setActiveForm('detail')}>
						Back
					</Button>

					<Button variant="primary" onClick={handleSubmit}>
						{formData.id ? 'Save' : 'Create New Segment'}
					</Button>
				</div>
			</div>
		</>
	);
};

export default SegmentSummary;
